import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import CallAnalysisSection from '../components/CallAnalysisSection';
import { StatusBadge } from '../components/ui/status-badge';
import { IntentBadge, NextActionBadge } from '../components/ui/intent-badges';
import { getIntent, getNextAction } from '../lib/analysis-utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Upload,
  FileText,
  Loader2,
  Play,
  Pause,
  Volume2,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Phone,
  Clock,
  AlertCircle,
  Download,
  ExternalLink,
  Brain
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [campaignReports, setCampaignReports] = useState({});
  const [loadingReport, setLoadingReport] = useState({});
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    name: '',
    file: null,
    analysis_prompt: '',
    voice_agent_id: ''
  });
  const [voiceAgents, setVoiceAgents] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCampaigns();
    fetchVoiceAgents();
  }, []);

  const fetchVoiceAgents = async () => {
    try {
      const response = await axios.get(`${API}/voice-agents`);
      setVoiceAgents(response.data);
    } catch (error) {
      // silently fail
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/bulk-campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to fetch bulk campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadForm({ ...uploadForm, file });
    } else {
      toast.error('Please select a CSV or Excel file');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.name) {
      toast.error('Please provide campaign name and CSV file');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    
    try {
      let uploadUrl = `${API}/bulk-campaigns/upload?name=${encodeURIComponent(uploadForm.name)}`;
      if (uploadForm.analysis_prompt) {
        uploadUrl += `&analysis_prompt=${encodeURIComponent(uploadForm.analysis_prompt)}`;
      }
      if (uploadForm.voice_agent_id) {
        uploadUrl += `&voice_agent_id=${encodeURIComponent(uploadForm.voice_agent_id)}`;
      }
      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success(`Bulk campaign created with ${response.data.total_contacts} contacts!`);
      setUploadDialogOpen(false);
      setUploadForm({ name: '', file: null, analysis_prompt: '', voice_agent_id: '' });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const fetchCampaignReport = async (campaignId) => {
    setLoadingReport(prev => ({ ...prev, [campaignId]: true }));
    try {
      const response = await axios.get(`${API}/bulk-campaigns/${campaignId}`);
      setCampaignReports(prev => ({ ...prev, [campaignId]: response.data }));
    } catch (error) {
      toast.error('Failed to fetch campaign report');
    } finally {
      setLoadingReport(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const toggleExpand = (campaignId) => {
    const isExpanding = !expandedCampaigns[campaignId];
    setExpandedCampaigns(prev => ({ ...prev, [campaignId]: isExpanding }));
    if (isExpanding && !campaignReports[campaignId]) {
      fetchCampaignReport(campaignId);
    }
  };

  const pauseCampaign = async (campaignId) => {
    try {
      await axios.post(`${API}/bulk-campaigns/${campaignId}/pause`);
      toast.success('Campaign paused');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const resumeCampaign = async (campaignId) => {
    try {
      await axios.post(`${API}/bulk-campaigns/${campaignId}/resume`);
      toast.success('Campaign resumed');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to resume campaign');
    }
  };

  const exportCampaign = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/bulk-campaigns/${campaignId}/export`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'campaign_report.csv';
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this bulk campaign?')) return;
    try {
      await axios.delete(`${API}/bulk-campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const downloadSampleCSV = () => {
    const csv = 'name,phone_number,company,designation\nJohn Doe,+1234567890,Acme Corp,CTO\nJane Smith,+0987654321,TechCo,VP Sales';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openCallDetails = (call) => {
    setSelectedCall(call);
    setCallDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-gray-200" />
          <Skeleton className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bulk-campaigns-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Bulk Campaigns</h1>
            <p className="text-gray-500 mt-1">Upload CSV to create bulk call campaigns</p>
          </div>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="upload-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="font-heading text-gray-900">Create Bulk Campaign</DialogTitle>
                <DialogDescription className="text-gray-500">
                  Upload a CSV/Excel file with property leads to create a bulk calling campaign
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Campaign Name</Label>
                  <Input
                    data-testid="bulk-campaign-name-input"
                    placeholder="e.g., Skyline Towers - Phase 2 Leads"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">CSV File</Label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-300 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="csv-file-input"
                    />
                    {uploadForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-400">
                        <FileText className="w-5 h-5" />
                        <span>{uploadForm.file.name}</span>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <Upload className="w-8 h-8 mx-auto mb-2" />
                        <p>Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500 mt-1">CSV or Excel file</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Voice Agent Selection */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Voice Agent</Label>
                  <select
                    value={uploadForm.voice_agent_id}
                    onChange={(e) => setUploadForm({ ...uploadForm, voice_agent_id: e.target.value })}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    required
                  >
                    <option value="">Select a voice agent...</option>
                    {voiceAgents.filter(a => a.is_active).map(agent => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                  {voiceAgents.length === 0 && (
                    <p className="text-xs text-amber-600">No voice agents yet. Create one in the Voice Agents page first.</p>
                  )}
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">
                      CSV needs: <code className="text-blue-400">phone_number</code> (required), <code className="text-blue-400">name</code> (optional). Any extra columns become initial context for the call. Max 100 rows.
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="text-blue-400 p-0 h-auto"
                      onClick={downloadSampleCSV}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download sample CSV
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Analysis Prompt (Optional)</Label>
                  <p className="text-xs text-gray-500">
                    Define how AI should analyze completed call transcripts. Leave empty to use agent's analysis prompt or default analysis.
                  </p>
                  <Textarea
                    data-testid="bulk-analysis-prompt-input"
                    placeholder="e.g., Analyze this real estate cold call. Return JSON with: sentiment, interest_level, intent, next_action, key_objections, summary..."
                    value={uploadForm.analysis_prompt}
                    onChange={(e) => setUploadForm({ ...uploadForm, analysis_prompt: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900 min-h-[80px]"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setUploadDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700" 
                    disabled={uploading || !uploadForm.file}
                    data-testid="submit-bulk-campaign-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Campaign
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-16 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No bulk campaigns yet. Upload a CSV/Excel with property leads to get started!</p>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setUploadDialogOpen(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <BulkCampaignCard
                key={campaign.id}
                campaign={campaign}
                expanded={expandedCampaigns[campaign.id]}
                report={campaignReports[campaign.id]}
                loadingReport={loadingReport[campaign.id]}
                onToggle={() => toggleExpand(campaign.id)}
                onRefresh={() => fetchCampaignReport(campaign.id)}
                onPause={() => pauseCampaign(campaign.id)}
                onResume={() => resumeCampaign(campaign.id)}
                onDelete={() => deleteCampaign(campaign.id)}
                onExport={() => exportCampaign(campaign.id)}
                onViewCall={openCallDetails}
              />
            ))}
          </div>
        )}

        {/* Call Details Dialog */}
        <CallDetailsDialog
          call={selectedCall}
          open={callDialogOpen}
          onOpenChange={setCallDialogOpen}
          onReanalyze={async (callId) => {
            try {
              await axios.post(`${API}/calls/${callId}/analyze`);
              toast.success('Analysis triggered. Refresh in a few seconds to see results.');
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to trigger analysis');
            }
          }}
        />
      </div>
    </DashboardLayout>
  );
}

function BulkCampaignCard({ campaign, expanded, report, loadingReport, onToggle, onRefresh, onPause, onResume, onDelete, onViewCall, onExport }) {
  const progress = campaign.total_contacts > 0 
    ? ((campaign.completed_calls + campaign.failed_calls + campaign.busy_calls + campaign.noanswer_calls) / campaign.total_contacts * 100)
    : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'completed': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'paused': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid={`bulk-campaign-card-${campaign.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <CardTitle className="font-heading text-lg text-gray-900">{campaign.name}</CardTitle>
              <Badge className={`${getStatusColor(campaign.status)} border`}>
                {campaign.status === 'running' && (
                  <span className="relative flex h-2 w-2 mr-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400" />
                  </span>
                )}
                {campaign.status}
              </Badge>
            </div>
            <CardDescription className="mt-1">
              <span className="text-gray-500">{campaign.total_contacts} contacts</span>
              {campaign.voice_agent_name && (
                <>
                  <span className="text-gray-500 mx-2">•</span>
                  <span className="text-blue-500 text-xs font-medium">Agent: {campaign.voice_agent_name}</span>
                </>
              )}
              <span className="text-gray-500 mx-2">•</span>
              <span className="text-gray-500">Created {format(new Date(campaign.created_at), 'MMM d, yyyy HH:mm')}</span>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {campaign.status === 'running' ? (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                onClick={onPause}
                data-testid={`pause-campaign-${campaign.id}-btn`}
              >
                <Pause className="w-4 h-4 mr-1" />
                Pause
              </Button>
            ) : campaign.status === 'paused' ? (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={onResume}
                data-testid={`resume-campaign-${campaign.id}-btn`}
              >
                <Play className="w-4 h-4 mr-1" />
                Resume
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-blue-400"
              onClick={onExport}
              title="Export report as CSV"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-rose-400"
              onClick={onDelete}
              data-testid={`delete-bulk-campaign-${campaign.id}-btn`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-500"
              onClick={onToggle}
              data-testid={`expand-bulk-campaign-${campaign.id}-btn`}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          <div className="p-2 rounded bg-gray-100 text-center">
            <p className="text-xs text-gray-500">Completed</p>
            <p className="font-mono text-emerald-400">{campaign.completed_calls}</p>
          </div>
          <div className="p-2 rounded bg-gray-100 text-center">
            <p className="text-xs text-gray-500">Failed</p>
            <p className="font-mono text-rose-400">{campaign.failed_calls}</p>
          </div>
          <div className="p-2 rounded bg-gray-100 text-center">
            <p className="text-xs text-gray-500">Busy</p>
            <p className="font-mono text-orange-400">{campaign.busy_calls}</p>
          </div>
          <div className="p-2 rounded bg-gray-100 text-center">
            <p className="text-xs text-gray-500">No Answer</p>
            <p className="font-mono text-purple-400">{campaign.noanswer_calls}</p>
          </div>
          <div className="p-2 rounded bg-gray-100 text-center">
            <p className="text-xs text-gray-500">In Progress</p>
            <p className="font-mono text-blue-400">{campaign.in_progress_calls}</p>
          </div>
        </div>
      </CardHeader>
      
      {/* Expanded Report */}
      {expanded && (
        <CardContent className="pt-0">
          <div className="border-t border-gray-200 mt-4 pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-500">Call Details</p>
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-500 hover:text-gray-900"
                onClick={onRefresh}
                data-testid={`refresh-bulk-report-${campaign.id}-btn`}
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${loadingReport ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {loadingReport ? (
              <div className="space-y-2">
                <Skeleton className="h-10 bg-gray-200" />
                <Skeleton className="h-10 bg-gray-200" />
                <Skeleton className="h-10 bg-gray-200" />
              </div>
            ) : report?.calls?.length > 0 ? (
              <ScrollArea className="max-h-96">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-300 hover:bg-transparent">
                      <TableHead className="text-gray-500 text-xs">Contact</TableHead>
                      <TableHead className="text-gray-500 text-xs">Phone</TableHead>
                      <TableHead className="text-gray-500 text-xs">Status</TableHead>
                      <TableHead className="text-gray-500 text-xs">Intent</TableHead>
                      <TableHead className="text-gray-500 text-xs">Next Action</TableHead>
                      <TableHead className="text-gray-500 text-xs">Time</TableHead>
                      <TableHead className="text-gray-500 text-xs text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.calls.map((call) => (
                      <TableRow 
                        key={call.id} 
                        className="border-gray-300 hover:bg-gray-50 cursor-pointer"
                        onClick={() => onViewCall(call)}
                        data-testid={`bulk-call-row-${call.id}`}
                      >
                        <TableCell className="text-sm text-gray-700">{call.contact_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm text-gray-700">{call.phone_number}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={call.status} />
                            {call.analysis_status === 'completed' && (
                              <Brain className="w-3.5 h-3.5 text-blue-400" title="Analysis available" />
                            )}
                            {call.analysis_status === 'running' && (
                              <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" title="Analyzing..." />
                            )}
                            {call.analysis_status === 'failed' && (
                              <AlertCircle className="w-3.5 h-3.5 text-rose-400" title="Analysis failed" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell><IntentBadge intent={getIntent(call.analysis_result)} /></TableCell>
                        <TableCell><NextActionBadge action={getNextAction(call.analysis_result)} /></TableCell>
                        <TableCell className="text-gray-500 text-xs">
                          {format(new Date(call.updated_at), 'HH:mm:ss')}
                        </TableCell>
                        <TableCell className="text-right">
                          {(call.recording_url || call.transcript_text) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-gray-500 hover:text-gray-900"
                            >
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No calls yet</p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function CallDetailsDialog({ call, open, onOpenChange, onReanalyze }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  if (!call) return null;

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="font-heading text-gray-900">
              {call.contact_name || 'Call Details'}
            </DialogTitle>
            <StatusBadge status={call.status} />
          </div>
          <DialogDescription className="text-gray-500">
            <span className="font-mono">{call.phone_number}</span>
            <span className="mx-2">•</span>
            {format(new Date(call.created_at), 'MMMM d, yyyy HH:mm')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Prompt Used */}
            {call.prompt && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Prompt Used</span>
                </div>
                <p className="text-sm text-gray-500">{call.prompt}</p>
              </div>
            )}

            {/* Audio Player */}
            {call.recording_url && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <Volume2 className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Recording</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-300"
                    onClick={togglePlay}
                    data-testid="play-recording-btn"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <audio
                    ref={audioRef}
                    src={call.recording_url}
                    onEnded={() => setIsPlaying(false)}
                    className="flex-1"
                    controls
                  />
                </div>
                <a
                  href={call.recording_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {/* Transcript */}
            {(call.transcript_text || call.transcript_url) && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Transcript</span>
                </div>
                {call.transcript_text ? (
                  <div className="font-mono text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded-lg max-h-48 overflow-auto">
                    {call.transcript_text}
                  </div>
                ) : call.transcript_url ? (
                  <a
                    href={call.transcript_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    View transcript <ExternalLink className="w-3 h-3" />
                  </a>
                ) : null}
              </div>
            )}

            {/* Call Analysis */}
            <CallAnalysisSection
              call={call}
              onReanalyze={onReanalyze ? () => onReanalyze(call.id) : undefined}
            />

            {/* Gathered Context */}
            {call.gathered_context && Object.keys(call.gathered_context).length > 0 && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Call Context</span>
                </div>
                <div className="font-mono text-xs text-gray-500 bg-white p-3 rounded-lg overflow-auto max-h-48">
                  <pre>{JSON.stringify(call.gathered_context, null, 2)}</pre>
                </div>
              </div>
            )}

            {/* No additional data */}
            {!call.recording_url && !call.transcript_text && !call.transcript_url &&
             (!call.gathered_context || Object.keys(call.gathered_context).length === 0) && (
              <div className="text-center py-8">
                <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  {call.status === 'completed'
                    ? 'Call completed. Additional details may be available soon.'
                    : call.status === 'pending'
                    ? 'Call is pending in queue.'
                    : 'Call details will appear here once the call is completed.'}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
